import { useState } from "react";
import { useApp } from "@/App";
import { useFetch } from "@/lib/hooks";
import { api } from "@/lib/api";
import type { SwapProposal, Item } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FairnessGauge } from "@/components/FairnessGauge";
import { ArrowLeftRight, Check, X, MessageSquare, Clock, ArrowRight } from "lucide-react";

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; color: string }> = {
	pending: { label: "Pending", variant: "secondary", color: "bg-amber-100 text-amber-800" },
	accepted: { label: "Accepted", variant: "default", color: "bg-emerald-100 text-emerald-800" },
	declined: { label: "Declined", variant: "destructive", color: "bg-red-100 text-red-800" },
	countered: { label: "Countered", variant: "outline", color: "bg-blue-100 text-blue-800" },
	completed: { label: "Completed", variant: "default", color: "bg-green-100 text-green-800" },
	cancelled: { label: "Cancelled", variant: "outline", color: "bg-gray-100 text-gray-800" },
};

function SwapCard({ swap, items, isIncoming }: { swap: SwapProposal; items: Item[]; isIncoming: boolean }) {
	const { navigate, currentUserId } = useApp();
	const { refetch } = useFetch(() => api.users.getSwaps(currentUserId), [currentUserId]);
	const [updating, setUpdating] = useState(false);

	const offeredItems = swap.offeredItems.map((id) => items.find((i) => i.id === id)).filter(Boolean) as Item[];
	const requestedItems = swap.requestedItems.map((id) => items.find((i) => i.id === id)).filter(Boolean) as Item[];

	const displayOffered = isIncoming ? offeredItems : offeredItems;
	const displayRequested = isIncoming ? requestedItems : requestedItems;
	const offeredLabel = isIncoming ? "They offer" : "You offer";
	const requestedLabel = isIncoming ? "They want" : "You want";

	const handleAction = async (status: string) => {
		setUpdating(true);
		try {
			await api.swaps.updateStatus(swap.id, status);
			window.location.reload();
		} finally {
			setUpdating(false);
		}
	};

	const config = statusConfig[swap.status] || statusConfig.pending;

	return (
		<Card className="overflow-hidden">
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2">
						<ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
						<CardTitle className="text-sm">
							{isIncoming ? (
								<>From <button className="text-primary underline" onClick={() => navigate(`/profile/${swap.fromUserId}`)}>{swap.fromUsername}</button></>
							) : (
								<>To <button className="text-primary underline" onClick={() => navigate(`/profile/${swap.toUserId}`)}>{swap.toUsername}</button></>
							)}
						</CardTitle>
					</div>
					<Badge className={`text-[10px] ${config.color}`}>
						{config.label}
					</Badge>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-start">
					<div className="space-y-1.5">
						<p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{offeredLabel}</p>
						{displayOffered.map((item) => (
							<div key={item.id} className="text-xs p-2 rounded bg-muted/50">
								<p className="font-medium truncate">{item.title}</p>
								<p className="text-muted-foreground">${item.estimatedValue}</p>
							</div>
						))}
					</div>
					<div className="flex flex-col items-center gap-2 pt-5">
						<ArrowRight className="h-4 w-4 text-muted-foreground rotate-0" />
						<div className="w-16">
							<FairnessGauge score={swap.fairnessScore} size="sm" />
						</div>
					</div>
					<div className="space-y-1.5">
						<p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{requestedLabel}</p>
						{displayRequested.map((item) => (
							<div key={item.id} className="text-xs p-2 rounded bg-muted/50">
								<p className="font-medium truncate">{item.title}</p>
								<p className="text-muted-foreground">${item.estimatedValue}</p>
							</div>
						))}
					</div>
				</div>

				{swap.message && (
					<div className="flex gap-2 p-3 rounded-lg bg-muted/30 border">
						<MessageSquare className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
						<p className="text-xs text-muted-foreground">{swap.message}</p>
					</div>
				)}

				<div className="flex items-center justify-between text-xs text-muted-foreground">
					<div className="flex items-center gap-1">
						<Clock className="h-3 w-3" />
						{swap.createdAt}
					</div>
				</div>

				{swap.status === "pending" && isIncoming && (
					<div className="flex gap-2">
						<Button
							size="sm"
							className="flex-1"
							onClick={() => handleAction("accepted")}
							disabled={updating}
						>
							<Check className="h-3.5 w-3.5 mr-1" />
							Accept
						</Button>
						<Button
							size="sm"
							variant="outline"
							className="flex-1"
							onClick={() => navigate(`/trade/${swap.fromUserId}`)}
						>
							Counter
						</Button>
						<Button
							size="sm"
							variant="destructive"
							className="flex-1"
							onClick={() => handleAction("declined")}
							disabled={updating}
						>
							<X className="h-3.5 w-3.5 mr-1" />
							Decline
						</Button>
					</div>
				)}

				{swap.status === "accepted" && (
					<Button
						size="sm"
						className="w-full"
						onClick={() => handleAction("completed")}
						disabled={updating}
					>
						<Check className="h-3.5 w-3.5 mr-1" />
						Mark as Completed
					</Button>
				)}

				{swap.status === "pending" && !isIncoming && (
					<Button
						size="sm"
						variant="outline"
						className="w-full"
						onClick={() => handleAction("cancelled")}
						disabled={updating}
					>
						Cancel Proposal
					</Button>
				)}
			</CardContent>
		</Card>
	);
}

export function SwapsPage() {
	const { currentUserId } = useApp();
	const { data: swaps, loading } = useFetch(() => api.swaps.list(), []);
	const { data: allItems } = useFetch(() => api.items.listAll(), []);

	const mySwaps = swaps?.filter((s) => s.fromUserId === currentUserId || s.toUserId === currentUserId) || [];
	const incoming = mySwaps.filter((s) => s.toUserId === currentUserId);
	const outgoing = mySwaps.filter((s) => s.fromUserId === currentUserId);
	const active = mySwaps.filter((s) => ["pending", "accepted", "countered"].includes(s.status));
	const history = mySwaps.filter((s) => ["completed", "declined", "cancelled"].includes(s.status));

	if (loading) {
		return (
			<div className="space-y-4">
				<div className="h-8 bg-muted rounded w-48 animate-pulse" />
				{Array.from({ length: 3 }).map((_, i) => (
					<div key={i} className="h-48 bg-muted rounded-lg animate-pulse" />
				))}
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto space-y-6">
			<div>
				<h1 className="text-3xl font-bold tracking-tight">My Swaps</h1>
				<p className="text-muted-foreground mt-1">
					Manage your swap proposals and trade history.
				</p>
			</div>

			<div className="grid grid-cols-3 gap-4">
				<Card>
					<CardContent className="pt-6 text-center">
						<p className="text-3xl font-bold">{active.length}</p>
						<p className="text-xs text-muted-foreground">Active</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6 text-center">
						<p className="text-3xl font-bold">{incoming.filter((s) => s.status === "pending").length}</p>
						<p className="text-xs text-muted-foreground">Pending Review</p>
					</CardContent>
				</Card>
				<Card>
					<CardContent className="pt-6 text-center">
						<p className="text-3xl font-bold">{history.filter((s) => s.status === "completed").length}</p>
						<p className="text-xs text-muted-foreground">Completed</p>
					</CardContent>
				</Card>
			</div>

			<Tabs defaultValue="incoming">
				<TabsList className="w-full">
					<TabsTrigger value="incoming" className="flex-1">
						Incoming ({incoming.length})
					</TabsTrigger>
					<TabsTrigger value="outgoing" className="flex-1">
						Outgoing ({outgoing.length})
					</TabsTrigger>
					<TabsTrigger value="history" className="flex-1">
						History ({history.length})
					</TabsTrigger>
				</TabsList>

				<TabsContent value="incoming" className="space-y-4 mt-4">
					{incoming.length === 0 ? (
						<p className="text-center text-muted-foreground py-8">No incoming proposals.</p>
					) : (
						incoming.map((swap) => (
							<SwapCard key={swap.id} swap={swap} items={allItems || []} isIncoming />
						))
					)}
				</TabsContent>

				<TabsContent value="outgoing" className="space-y-4 mt-4">
					{outgoing.length === 0 ? (
						<p className="text-center text-muted-foreground py-8">No outgoing proposals.</p>
					) : (
						outgoing.map((swap) => (
							<SwapCard key={swap.id} swap={swap} items={allItems || []} isIncoming={false} />
						))
					)}
				</TabsContent>

				<TabsContent value="history" className="space-y-4 mt-4">
					{history.length === 0 ? (
						<p className="text-center text-muted-foreground py-8">No completed or cancelled swaps yet.</p>
					) : (
						history.map((swap) => (
							<SwapCard
								key={swap.id}
								swap={swap}
								items={allItems || []}
								isIncoming={swap.toUserId === currentUserId}
							/>
						))
					)}
				</TabsContent>
			</Tabs>
		</div>
	);
}
